<template>
  <teleport to="body">
    <transition name="hs-modal">
      <div v-if="modelValue" class="hs-modal" :style="{ zIndex }" role="dialog" aria-modal="true">
        <button v-if="closeOnBackdrop" class="hs-modal__backdrop" type="button" @click="close" />
        <section
          class="hs-modal__panel"
          :class="[`hs-modal__panel--${size}`, panelClass]"
          :style="panelStyle"
        >
          <header class="hs-modal__header">
            <div>
              <p v-if="kicker" class="hs-modal__kicker">{{ kicker }}</p>
              <h2>{{ title }}</h2>
              <p v-if="description" class="hs-modal__description">{{ description }}</p>
            </div>
            <hs-button variant="secondary" icon="x" icon-only aria-label="Close modal" @click="close" />
          </header>
          <div class="hs-modal__body" :class="bodyClass" :style="bodyStyle">
            <slot />
          </div>
          <footer v-if="$slots.footer" class="hs-modal__footer">
            <slot name="footer" />
          </footer>
        </section>
      </div>
    </transition>
  </teleport>
</template>

<script>
import HsButton from "./HsButton.vue";

export default {
  name: "HsModal",
  components: { HsButton },
  props: {
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: "" },
    kicker: { type: String, default: "" },
    description: { type: String, default: "" },
    size: {
      type: String,
      default: "md",
      validator: (value) => ["sm", "md", "lg", "xl"].includes(value),
    },
    closeOnBackdrop: { type: Boolean, default: true },
    zIndex: { type: [String, Number], default: 5200 },
    panelClass: { type: [String, Array, Object], default: "" },
    bodyClass: { type: [String, Array, Object], default: "" },
    panelStyle: { type: Object, default: null },
    bodyStyle: { type: Object, default: null },
  },
  emits: ["update:modelValue", "close"],
  watch: {
    modelValue: {
      handler(open) {
        document.body.style.overflow = open ? "hidden" : "auto";
      },
      immediate: true,
    },
  },
  beforeUnmount() {
    document.body.style.overflow = "auto";
  },
  methods: {
    close() {
      this.$emit("update:modelValue", false);
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.hs-modal {
  align-items: center;
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1rem;
  position: fixed;
}

.hs-modal__backdrop {
  background:
    radial-gradient(circle at 20% 8%, rgba(50, 147, 236, 0.18), transparent 30%),
    rgba(15, 23, 42, 0.62);
  backdrop-filter: blur(10px);
  inset: 0;
  position: absolute;
}

.hs-modal__panel {
  background:
    radial-gradient(circle at 88% 0%, color-mix(in srgb, var(--hs-primary, #3293ec) 10%, transparent), transparent 32%),
    color-mix(in srgb, var(--hs-surface, #ffffff) 96%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 28px;
  box-shadow: 0 28px 90px rgba(15, 23, 42, 0.28);
  display: flex;
  flex-direction: column;
  max-height: min(88vh, 900px);
  overflow: hidden;
  position: relative;
  width: min(100%, 720px);
}

.hs-modal__panel--sm { width: min(100%, 480px); }
.hs-modal__panel--lg { width: min(100%, 920px); }
.hs-modal__panel--xl { width: min(100%, 1120px); }

.hs-modal__header,
.hs-modal__footer {
  align-items: flex-start;
  border-bottom: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  display: flex;
  flex: 0 0 auto;
  gap: 1rem;
  justify-content: space-between;
  padding: 1.1rem;
}

.hs-modal__footer {
  align-items: center;
  border-bottom: 0;
  border-top: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  justify-content: flex-end;
}

.hs-modal__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: 1.1rem;
}

.hs-modal__kicker {
  color: var(--hs-primary, #3293ec);
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.12em;
  margin: 0 0 0.35rem;
  text-transform: uppercase;
}

.hs-modal h2 {
  color: var(--hs-text, #0f172a);
  font-size: 1.45rem;
  font-weight: 950;
  line-height: 1.1;
  margin: 0;
}

.hs-modal__description {
  color: var(--hs-muted, #64748b);
  font-size: 0.88rem;
  font-weight: 700;
  line-height: 1.55;
  margin: 0.45rem 0 0;
}

.hs-modal-enter-active,
.hs-modal-leave-active {
  transition: opacity 0.18s ease;
}

.hs-modal-enter-from,
.hs-modal-leave-to {
  opacity: 0;
}

.hs-modal-enter-active .hs-modal__panel,
.hs-modal-leave-active .hs-modal__panel {
  transition: transform 0.22s ease, opacity 0.22s ease;
}

.hs-modal-enter-from .hs-modal__panel,
.hs-modal-leave-to .hs-modal__panel {
  opacity: 0;
  transform: translateY(12px) scale(0.98);
}
</style>
